import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Bell,
  BellRing,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock3,
  Edit3,
  MessageSquareText,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import {
  deletePlannerTaskWithAudit,
  savePlannerTaskWithAudit,
  watchPlannerTasks,
} from '../planner/cloud'
import { buildTaskAuditEvent, buildTaskDeletionEvent } from '../planner/audit'
import {
  calendarDays,
  dateKey,
  isOverdue,
  localDateFromKey,
  reminderAt,
  sortAgendaTasks,
  sortTasks,
  tasksForDate,
  workWeekDays,
} from '../planner/utils'
import {
  TASK_PRIORITY_OPTIONS,
  type PlannerTask,
  type TaskPriority,
} from '../planner/types'

interface PlannerProps {
  userId: string
  mode?: 'compact' | 'full'
}

interface TaskDraft {
  id: string | null
  title: string
  notes: string
  date: string
  time: string
  priority: TaskPriority
  completed: boolean
  reminderMinutes: number | null
  createdAt: string | null
}

const REMINDER_OPTIONS = [
  { value: '', label: 'Без напоминания' },
  { value: '0', label: 'В момент задачи' },
  { value: '10', label: 'За 10 минут' },
  { value: '30', label: 'За 30 минут' },
  { value: '60', label: 'За 1 час' },
  { value: '1440', label: 'За 1 день' },
]

const weekdayFormatter = new Intl.DateTimeFormat('ru-RU', { weekday: 'short' })
const dayFormatter = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' })
const monthFormatter = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' })
const fullDateFormatter = new Intl.DateTimeFormat('ru-RU', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})
const agendaYearFormatter = new Intl.DateTimeFormat('ru-RU', { year: 'numeric' })
const timeFormatter = new Intl.DateTimeFormat('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
})

function defaultTaskTime(day: Date): string {
  const now = new Date()
  if (dateKey(day) !== dateKey(now)) return '09:00'
  const nextHour = Math.min(23, now.getHours() + 1)
  return `${String(nextHour).padStart(2, '0')}:00`
}

function newDraft(day = new Date()): TaskDraft {
  return {
    id: null,
    title: '',
    notes: '',
    date: dateKey(day),
    time: defaultTaskTime(day),
    priority: 'normal',
    completed: false,
    reminderMinutes: 30,
    createdAt: null,
  }
}

function draftFromTask(task: PlannerTask): TaskDraft {
  const due = new Date(task.dueAt)
  return {
    id: task.id,
    title: task.title,
    notes: task.notes,
    date: dateKey(due),
    time: `${String(due.getHours()).padStart(2, '0')}:${String(due.getMinutes()).padStart(2, '0')}`,
    priority: task.priority,
    completed: task.completed,
    reminderMinutes: task.reminderMinutes,
    createdAt: task.createdAt,
  }
}

function draftSignature(draft: TaskDraft): string {
  return JSON.stringify({
    id: draft.id,
    title: draft.title,
    notes: draft.notes,
    date: draft.date,
    time: draft.time,
    priority: draft.priority,
    completed: draft.completed,
    reminderMinutes: draft.reminderMinutes,
  })
}

function priorityLabel(priority: TaskPriority): string {
  return TASK_PRIORITY_OPTIONS.find((option) => option.value === priority)?.label ?? priority
}

function taskNotePreview(notes: string): string {
  return notes
    .split('\n')
    .map((line) => line.trim())
    .filter(
      (line) =>
        line &&
        !line.startsWith('Источник:') &&
        line !== 'Автоматически создано из быстрой фиксации контакта.',
    )
    .join(' · ')
    .replace(/^Результат:\s*/i, '')
    .slice(0, 180)
}

export function Planner({ userId, mode = 'compact' }: PlannerProps) {
  const [tasks, setTasks] = useState<PlannerTask[]>([])
  const [expanded, setExpanded] = useState(mode === 'full')
  const [month, setMonth] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  )
  const [selectedDay, setSelectedDay] = useState(() => dateKey(new Date()))
  const [draft, setDraft] = useState<TaskDraft | null>(null)
  const [draftBaseline, setDraftBaseline] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [reminderMessage, setReminderMessage] = useState('')
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>(() =>
      typeof Notification === 'undefined' ? 'denied' : Notification.permission,
    )
  const detailsPanelRef = useRef<HTMLElement | null>(null)
  const hasUnsavedDraft = draft !== null && draftBaseline !== draftSignature(draft)

  useEffect(() => {
    setExpanded(mode === 'full')
  }, [mode])

  useEffect(() => {
    return watchPlannerTasks(
      userId,
      (remoteTasks) => setTasks(sortTasks(remoteTasks)),
      () => setMessage('Не удалось синхронизировать ежедневник.'),
    )
  }, [userId])

  useEffect(() => {
    if (!message) return
    const timer = window.setTimeout(() => setMessage(''), 3800)
    return () => window.clearTimeout(timer)
  }, [message])

  useEffect(() => {
    if (!hasUnsavedDraft) return
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeLeaving)
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving)
  }, [hasUnsavedDraft])

  useEffect(() => {
    function checkReminders() {
      const now = Date.now()
      tasks.forEach((task) => {
        const trigger = reminderAt(task)
        if (task.completed || trigger === null || trigger > now) return
        if (now > new Date(task.dueAt).getTime() + 4 * 60 * 60_000) return

        const key = `worksmarter-reminder:${task.id}:${task.updatedAt}`
        if (localStorage.getItem(key)) return
        localStorage.setItem(key, 'shown')
        setReminderMessage(`Напоминание: ${task.title}`)

        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          new Notification('WorkSmarter · задача', {
            body: `${task.title} · ${timeFormatter.format(new Date(task.dueAt))}`,
            tag: task.id,
          })
        }
      })
    }

    checkReminders()
    const timer = window.setInterval(checkReminders, 30_000)
    return () => window.clearInterval(timer)
  }, [tasks])

  const today = useMemo(() => new Date(), [])
  const todayTasks = useMemo(() => tasksForDate(tasks, new Date()), [tasks])
  const weekDays = useMemo(() => workWeekDays(new Date()), [])
  const monthDays = useMemo(() => calendarDays(month), [month])
  const openTasks = tasks.filter((task) => !task.completed).length
  const selectedDate = useMemo(() => localDateFromKey(selectedDay), [selectedDay])
  const selectedDayTasks = useMemo(
    () => sortAgendaTasks(tasksForDate(tasks, selectedDate)),
    [selectedDate, tasks],
  )
  const selectedDayActiveTasks = selectedDayTasks.filter((task) => !task.completed)
  const selectedDayCompletedTasks = selectedDayTasks.filter((task) => task.completed)

  function revealDetailsPanel(focusPanel = false) {
    window.setTimeout(() => {
      const panel = detailsPanelRef.current
      if (!panel) return
      if (window.matchMedia('(max-width: 1100px)').matches) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      if (focusPanel) panel.focus({ preventScroll: true })
    })
  }

  function selectMonthFor(day: Date) {
    if (day.getMonth() === month.getMonth() && day.getFullYear() === month.getFullYear()) return
    setMonth(new Date(day.getFullYear(), day.getMonth(), 1))
  }

  function clearDraft() {
    setDraft(null)
    setDraftBaseline(null)
  }

  function canLeaveDraft(): boolean {
    if (!hasUnsavedDraft) return true
    return window.confirm('Есть несохранённые изменения задачи. Закрыть их без сохранения?')
  }

  function openDay(day: Date) {
    if (!canLeaveDraft()) return
    setExpanded(true)
    setSelectedDay(dateKey(day))
    selectMonthFor(day)
    clearDraft()
    revealDetailsPanel(true)
  }

  function openNewTask(day = new Date()) {
    if (!canLeaveDraft()) return
    const nextDraft = newDraft(day)
    setExpanded(true)
    setSelectedDay(dateKey(day))
    selectMonthFor(day)
    setDraft(nextDraft)
    setDraftBaseline(draftSignature(nextDraft))
    revealDetailsPanel()
  }

  function openTask(task: PlannerTask) {
    if (draft?.id === task.id) {
      setExpanded(true)
      revealDetailsPanel()
      return
    }
    if (!canLeaveDraft()) return
    const due = new Date(task.dueAt)
    const nextDraft = draftFromTask(task)
    setExpanded(true)
    setSelectedDay(dateKey(due))
    selectMonthFor(due)
    setDraft(nextDraft)
    setDraftBaseline(draftSignature(nextDraft))
    revealDetailsPanel()
  }

  function closeEditor() {
    if (!canLeaveDraft()) return
    clearDraft()
  }

  async function persistTask(
    task: PlannerTask,
    auditEvent: ReturnType<typeof buildTaskAuditEvent>,
    successMessage: string,
  ) {
    const previous = tasks
    setTasks((current) =>
      sortTasks([task, ...current.filter((item) => item.id !== task.id)]),
    )
    setSaving(true)
    try {
      await savePlannerTaskWithAudit(userId, task, auditEvent)
      setMessage(successMessage)
    } catch {
      setTasks(previous)
      setMessage('Не удалось сохранить задачу.')
      throw new Error('planner-save-failed')
    } finally {
      setSaving(false)
    }
  }

  async function submitDraft(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!draft || !draft.title.trim()) return

    const due = new Date(`${draft.date}T${draft.time || '09:00'}`)
    if (!Number.isFinite(due.getTime())) {
      setMessage('Проверь дату и время задачи.')
      return
    }

    const now = new Date().toISOString()
    const task: PlannerTask = {
      id: draft.id ?? crypto.randomUUID(),
      title: draft.title.trim(),
      notes: draft.notes.trim(),
      dueAt: due.toISOString(),
      priority: draft.priority,
      completed: draft.completed,
      reminderMinutes: draft.reminderMinutes,
      createdAt: draft.createdAt ?? now,
      updatedAt: now,
    }

    const previousTask = draft.id
      ? tasks.find((item) => item.id === draft.id) ?? null
      : null
    const auditEvent = buildTaskAuditEvent(previousTask, task, now)

    try {
      await persistTask(
        task,
        auditEvent,
        draft.id ? 'Задача обновлена' : 'Задача добавлена',
      )
      const savedDay = localDateFromKey(draft.date)
      setSelectedDay(draft.date)
      selectMonthFor(savedDay)
      clearDraft()
    } catch {
      // Ошибка уже отражена в интерфейсе.
    }
  }

  async function toggleTask(task: PlannerTask) {
    const updated = {
      ...task,
      completed: !task.completed,
      updatedAt: new Date().toISOString(),
    }
    const auditEvent = buildTaskAuditEvent(task, updated, updated.updatedAt)
    try {
      await persistTask(
        updated,
        auditEvent,
        updated.completed ? 'Задача выполнена' : 'Задача возвращена',
      )
    } catch {
      // Ошибка уже отражена в интерфейсе.
    }
  }

  async function removeTask(task: PlannerTask) {
    if (!window.confirm(`Удалить задачу «${task.title}»?`)) return
    const previous = tasks
    setTasks((current) => current.filter((item) => item.id !== task.id))
    setSaving(true)
    try {
      const occurredAt = new Date().toISOString()
      await deletePlannerTaskWithAudit(
        userId,
        task.id,
        buildTaskDeletionEvent(task, occurredAt),
      )
      if (draft?.id === task.id) clearDraft()
      setMessage('Задача удалена')
    } catch {
      setTasks(previous)
      setMessage('Не удалось удалить задачу.')
    } finally {
      setSaving(false)
    }
  }

  async function requestNotifications() {
    if (typeof Notification === 'undefined') {
      setMessage('Этот браузер не поддерживает системные уведомления.')
      return
    }
    const permission = await Notification.requestPermission()
    setNotificationPermission(permission)
    setMessage(
      permission === 'granted'
        ? 'Напоминания разрешены'
        : 'Браузер не разрешил уведомления',
    )
  }

  function moveMonth(delta: number) {
    if (!canLeaveDraft()) return
    const nextMonth = new Date(month.getFullYear(), month.getMonth() + delta, 1)
    setMonth(nextMonth)
    setSelectedDay(dateKey(nextMonth))
    clearDraft()
  }

  return (
    <section
      className={`planner-shell ${expanded ? 'is-expanded' : ''} planner-mode-${mode}`}
      aria-label="Ежедневник"
    >
      <header className="planner-header">
        <div className="planner-title">
          <span className="planner-icon"><CalendarDays size={21} /></span>
          <div>
            <span className="eyebrow">Ежедневник</span>
            <h2>{fullDateFormatter.format(today)}</h2>
          </div>
        </div>
        <div className="planner-actions">
          {notificationPermission !== 'granted' ? (
            <button
              className="planner-icon-button"
              type="button"
              onClick={() => void requestNotifications()}
              title="Разрешить напоминания"
            >
              <Bell size={18} />
            </button>
          ) : (
            <span className="planner-notifications-on" title="Напоминания разрешены">
              <BellRing size={17} />
            </span>
          )}
          <button className="button secondary" type="button" onClick={() => openNewTask()}>
            <Plus size={17} /> Задача
          </button>
          <button
            className="planner-expand-button"
            type="button"
            onClick={() => {
              if (expanded) {
                setExpanded(false)
              } else if (draft) {
                setExpanded(true)
              } else {
                openDay(new Date())
              }
            }}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            {expanded ? 'Свернуть' : 'Календарь'}
          </button>
        </div>
      </header>

      {reminderMessage ? (
        <div className="planner-reminder-banner">
          <BellRing size={17} />
          <strong>{reminderMessage}</strong>
          <button
            type="button"
            onClick={() => setReminderMessage('')}
            aria-label="Закрыть напоминание"
          >
            <X size={16} />
          </button>
        </div>
      ) : null}

      <div className="planner-summary">
        <div className="planner-today">
          <div className="planner-section-heading">
            <div>
              <span>Сегодня</span>
              <strong>{todayTasks.filter((task) => !task.completed).length} активных</strong>
            </div>
            <small>{openTasks} всего в работе</small>
          </div>
          <div className="planner-task-list">
            {todayTasks.length ? (
              todayTasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onToggle={() => void toggleTask(task)}
                  onEdit={() => openTask(task)}
                />
              ))
            ) : (
              <button className="planner-empty" type="button" onClick={() => openNewTask()}>
                <Plus size={18} /> На сегодня задач нет. Добавить первую.
              </button>
            )}
          </div>
        </div>

        <div className="planner-week">
          <div className="planner-section-heading compact">
            <div>
              <span>Рабочая неделя</span>
              <strong>Пн — Пт</strong>
            </div>
          </div>
          <div className="planner-week-days">
            {weekDays.map((day) => {
              const dayTasks = tasksForDate(tasks, day)
              const active = dayTasks.filter((task) => !task.completed)
              const highest = active[0]?.priority ?? 'low'
              return (
                <button
                  className={`planner-week-day priority-${highest} ${
                    dateKey(day) === dateKey(new Date()) ? 'is-today' : ''
                  }`}
                  type="button"
                  key={dateKey(day)}
                  onClick={() => openDay(day)}
                  title={`Открыть задачи за ${dayFormatter.format(day)}`}
                >
                  <span>{weekdayFormatter.format(day)}</span>
                  <strong>{day.getDate()}</strong>
                  <small>{active.length || '—'}</small>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {expanded ? (
        <div className="planner-expanded-content">
          <div className="planner-calendar-panel">
            <div className="planner-calendar-toolbar">
              <button type="button" onClick={() => moveMonth(-1)} aria-label="Предыдущий месяц">
                <ChevronLeft size={19} />
              </button>
              <strong>{monthFormatter.format(month)}</strong>
              <button type="button" onClick={() => moveMonth(1)} aria-label="Следующий месяц">
                <ChevronRight size={19} />
              </button>
            </div>
            <div className="planner-calendar-weekdays">
              {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>
            <div className="planner-calendar-grid">
              {monthDays.map((day) => {
                const dayTasks = tasksForDate(tasks, day)
                const outside = day.getMonth() !== month.getMonth()
                const key = dateKey(day)
                const remainingTasks = Math.max(0, dayTasks.length - 3)
                return (
                  <div
                    key={key}
                    className={`planner-calendar-day ${outside ? 'is-outside' : ''} ${
                      key === dateKey(new Date()) ? 'is-today' : ''
                    } ${
                      key === selectedDay ? 'is-selected' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="planner-day-open"
                      onClick={() => openDay(day)}
                      aria-label={`Показать все задачи за ${dayFormatter.format(day)}`}
                      aria-pressed={key === selectedDay}
                    >
                      <span className="planner-day-number">{day.getDate()}</span>
                      {dayTasks.length ? (
                        <span className="planner-day-count">{dayTasks.length}</span>
                      ) : null}
                    </button>
                    <button
                      type="button"
                      className="planner-day-add"
                      onClick={() => openNewTask(day)}
                      aria-label={`Добавить задачу на ${dayFormatter.format(day)}`}
                      title="Добавить задачу"
                    >
                      <Plus size={13} />
                    </button>
                    <div className="planner-day-tasks">
                      {dayTasks.slice(0, 3).map((task) => (
                        <button
                          type="button"
                          className={`planner-day-task priority-${task.priority} ${
                            task.completed ? 'is-completed' : ''
                          }`}
                          key={task.id}
                          title={`${task.title}${task.notes ? '\nОткрыть комментарий и детали' : ''}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            openTask(task)
                          }}
                        >
                          <span>{timeFormatter.format(new Date(task.dueAt))}</span>
                          <strong>{task.title}</strong>
                        </button>
                      ))}
                      {remainingTasks ? (
                        <button
                          className="planner-day-more"
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            openDay(day)
                          }}
                          aria-label={`Показать все ${dayTasks.length} задач за ${dayFormatter.format(day)}`}
                        >
                          Ещё {remainingTasks}
                        </button>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <aside className="planner-editor-panel" ref={detailsPanelRef} tabIndex={-1}>
            {draft ? (
              <form onSubmit={(event) => void submitDraft(event)}>
                <div className="planner-editor-header">
                  <div>
                    <span className="eyebrow">{draft.id ? 'Редактирование' : 'Новая задача'}</span>
                    <h3>{draft.id ? 'Изменить задачу' : 'Запланировать дело'}</h3>
                  </div>
                  <button
                    className="planner-back-to-day"
                    type="button"
                    onClick={closeEditor}
                    aria-label="Вернуться к задачам выбранного дня"
                  >
                    <ChevronLeft size={16} /> <span>К задачам дня</span>
                  </button>
                </div>

                <label>
                  <span>Задача</span>
                  <input
                    autoFocus
                    required
                    maxLength={300}
                    value={draft.title}
                    onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                    placeholder="Позвонить, подготовить КП, проверить заявку…"
                  />
                </label>

                <div className="planner-form-grid">
                  <label>
                    <span>Дата</span>
                    <input
                      type="date"
                      required
                      value={draft.date}
                      onChange={(event) => setDraft({ ...draft, date: event.target.value })}
                    />
                  </label>
                  <label>
                    <span>Время</span>
                    <input
                      type="time"
                      required
                      value={draft.time}
                      onChange={(event) => setDraft({ ...draft, time: event.target.value })}
                    />
                  </label>
                </div>

                <div className="planner-form-grid">
                  <label>
                    <span>Приоритет</span>
                    <select
                      value={draft.priority}
                      onChange={(event) =>
                        setDraft({ ...draft, priority: event.target.value as TaskPriority })
                      }
                    >
                      {TASK_PRIORITY_OPTIONS.map((option) => (
                        <option value={option.value} key={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Напомнить</span>
                    <select
                      value={draft.reminderMinutes === null ? '' : String(draft.reminderMinutes)}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          reminderMinutes:
                            event.target.value === '' ? null : Number(event.target.value),
                        })
                      }
                    >
                      {REMINDER_OPTIONS.map((option) => (
                        <option value={option.value} key={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <label>
                  <span>Комментарий</span>
                  <textarea
                    rows={5}
                    maxLength={5000}
                    value={draft.notes}
                    onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
                    placeholder="Детали, контакты, ожидаемый результат…"
                  />
                </label>

                {draft.id ? (
                  <label className="planner-completed-toggle">
                    <input
                      type="checkbox"
                      checked={draft.completed}
                      onChange={(event) =>
                        setDraft({ ...draft, completed: event.target.checked })
                      }
                    />
                    <span>Задача выполнена</span>
                  </label>
                ) : null}

                <div className="planner-editor-actions">
                  {draft.id ? (
                    <button
                      className="planner-delete-button"
                      type="button"
                      onClick={() => {
                        const task = tasks.find((item) => item.id === draft.id)
                        if (task) void removeTask(task)
                      }}
                    >
                      <Trash2 size={16} /> Удалить
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    className="button primary"
                    type="submit"
                    disabled={saving || !draft.title.trim()}
                  >
                    <Check size={17} /> {saving ? 'Сохраняю…' : 'Сохранить'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="planner-agenda">
                <div className="planner-agenda-header">
                  <div className="planner-agenda-date">
                    <strong>{selectedDate.getDate()}</strong>
                    <div>
                      <span className="eyebrow">Повестка дня</span>
                      <h3>{fullDateFormatter.format(selectedDate)}</h3>
                      <small>{agendaYearFormatter.format(selectedDate)}</small>
                    </div>
                  </div>
                  <button
                    className="button primary planner-agenda-add"
                    type="button"
                    onClick={() => openNewTask(selectedDate)}
                  >
                    <Plus size={17} /> Добавить
                  </button>
                </div>

                <div className="planner-agenda-overview">
                  <div>
                    <strong>{selectedDayTasks.length}</strong>
                    <span>всего</span>
                  </div>
                  <div>
                    <strong>{selectedDayActiveTasks.length}</strong>
                    <span>в работе</span>
                  </div>
                  <div>
                    <strong>{selectedDayCompletedTasks.length}</strong>
                    <span>готово</span>
                  </div>
                </div>

                {selectedDayTasks.length ? (
                  <div className="planner-agenda-groups">
                    {selectedDayActiveTasks.length ? (
                      <section className="planner-agenda-group">
                        <div className="planner-agenda-group-heading">
                          <span>В работе</span>
                          <small>{selectedDayActiveTasks.length}</small>
                        </div>
                        <div className="planner-agenda-list">
                          {selectedDayActiveTasks.map((task) => (
                            <TaskRow
                              key={task.id}
                              task={task}
                              onToggle={() => void toggleTask(task)}
                              onEdit={() => openTask(task)}
                            />
                          ))}
                        </div>
                      </section>
                    ) : null}

                    {selectedDayCompletedTasks.length ? (
                      <section className="planner-agenda-group is-completed">
                        <div className="planner-agenda-group-heading">
                          <span>Выполнено</span>
                          <small>{selectedDayCompletedTasks.length}</small>
                        </div>
                        <div className="planner-agenda-list">
                          {selectedDayCompletedTasks.map((task) => (
                            <TaskRow
                              key={task.id}
                              task={task}
                              onToggle={() => void toggleTask(task)}
                              onEdit={() => openTask(task)}
                            />
                          ))}
                        </div>
                      </section>
                    ) : null}
                  </div>
                ) : (
                  <button
                    className="planner-agenda-empty"
                    type="button"
                    onClick={() => openNewTask(selectedDate)}
                  >
                    <span><CalendarDays size={24} /></span>
                    <strong>День свободен</strong>
                    <small>Добавить звонок, встречу или рабочее дело</small>
                  </button>
                )}
              </div>
            )}
          </aside>
        </div>
      ) : null}

      {message ? <div className="planner-toast">{message}</div> : null}
    </section>
  )
}

interface TaskRowProps {
  task: PlannerTask
  onToggle: () => void
  onEdit: () => void
}

function TaskRow({ task, onToggle, onEdit }: TaskRowProps) {
  const notePreview = taskNotePreview(task.notes)

  return (
    <article
      className={`planner-task-row priority-${task.priority} ${
        task.completed ? 'is-completed' : ''
      } ${isOverdue(task) ? 'is-overdue' : ''}`}
    >
      <button
        className="planner-check"
        type="button"
        onClick={onToggle}
        aria-label={task.completed ? 'Вернуть задачу' : 'Отметить выполненной'}
      >
        {task.completed ? <Check size={14} /> : null}
      </button>
      <button className="planner-task-content" type="button" onClick={onEdit}>
        <span className="planner-task-title">{task.title}</span>
        {notePreview ? (
          <span className="planner-task-note-preview">
            <MessageSquareText size={12} /> {notePreview}
          </span>
        ) : null}
        <span className="planner-task-meta">
          <Clock3 size={13} /> {timeFormatter.format(new Date(task.dueAt))}
          <span className="planner-priority-label">{priorityLabel(task.priority)}</span>
          {task.reminderMinutes !== null ? <Bell size={12} /> : null}
          {isOverdue(task) ? <b>Просрочено</b> : null}
        </span>
      </button>
      <button
        className="planner-edit"
        type="button"
        onClick={onEdit}
        aria-label="Редактировать задачу"
      >
        <Edit3 size={15} />
      </button>
    </article>
  )
}
